/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import swal from "sweetalert";
import { useEdgesState } from 'reactflow';
import { useReactFlow } from 'reactflow';
import { useHistory } from 'react-router-dom';
import { usePost, useGet } from "seed/api";
import { useSet } from "seed/gql";
import { SET_EXECUTION } from "seed/gql/queries";
import { useNodeCustomState } from "components/studio/Controller.lib.state";
import l from "components/util/l";
import * as board from "components/studio/Controller.lib.board";
import * as save from "components/studio/Controller.lib.save";
import * as messages from "components/studio/Controller.lib.ui";
import View from "components/studio/Controller.view";

const Controller = forwardRef((props, ref) => {

  const {
    initialNodes = [],
    initialEdges = [],
    actions,
    flow,
    pages,
    user,
    flowHistory,
    flowHistoryPosition,
    activePageId,
    isOwner,
    isEditor,
    isAnonymous,
    isValidator,
    isCasePreview,
    setActivePageId,
    setHistoryPosition,
    reloadFlow,
    onPostSave,
    updateHistory
  } = props;

  const urlParams = new URLSearchParams(window.location.search);
  const resume = urlParams.get('resume');
  const userId = sessionStorage.getItem("id");
  const isDeveloper = sessionStorage.getItem("is_developer") == "true";
  const reactFlowWrapper = useRef(null);
  const wsExecutionRef = useRef(null);
  const wsNodeRef = useRef(null);
  const toolboxRef = useRef(null)
  const { setCenter, getZoom } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodeCustomState(initialNodes, (nodes) => save.saveNodePositions(nodes, isCasePreview, callSaveUi));
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isContactUsShown, setIsContactUsShown] = useState(false);
  const [isExecutionDetailsShown, setIsExecutionDetailsShown] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [showAnimations, setShowAnimations] = useState(flow?.user.accountSettings?.showAnimations ?? true);
  const [isAnimationOptionShown, setIsAnimationOptionShown] = useState(false);
  const [isToolboxCollapsed, setIsToolboxCollapsed] = useState(false)
  const [toolboxActiveSection, setToolboxActiveSection] = useState("actions");
  const [rightNavActiveSection, setRightNavActiveSection] = useState("executions")
  const [isRightNavCollapsed, setIsRightNavCollapsed] = useState(true);
  const callAutoSaveRef = useRef();
  const historyRouter = useHistory();

  const onClickExecuteNode = (remoteId) => {
    setNodes((nodes) => {
      board.executeNode(nodes, edges, remoteId, onCallExecute);
      return nodes;
    });
  }

  const reqLatestExecutions = useGet('/executions/get_latest_executions', { flow_id: flow.id });

  const [callResume, reqResume] = usePost("/executions/resume_execution");

  const [callGenerateFlow, reqGenerateFlow] = usePost("/flows/generate_flow", {
    onCompleted: () => reloadFlow()
  });

  const [callSetRead] = useSet(SET_EXECUTION);

  const [callDeleteNodesTree, reqDeleteNodesTree] = usePost("/flows/delete_nodes_tree", {
    onCompleted: () => {
      setSelectedNodes([]);
      historyRouter.replace(`/flow`);
      reloadFlow();
    },
    onError: () => {
      swal("Error", l("Ha ocurrido un error al eliminar el proceso, por favor intente de nuevo mas tarde%SA006"), "error");
    }
  });

  useEffect(() => {
    const [newNodes, newEdges, annotations] = board.initBoard(nodes, edges, flow.id, activePageId, isCasePreview, updateHistory,
      onClickExecuteNode, onClickChangeTriggerStatus, onClickAttachNode, onSelectNode, deleteNode, onClickInsertChild, callAutoSave);
    setEdges(newEdges);
    setNodes([...newNodes, ...annotations]);
    callCleanValidate({ flow_id: flow.id, page_id: activePageId })
  }, []);

  useEffect(() => {
    if (resume && reqResume.loading == false && reqResume.data == null)
      setTimeout(() => callResume({ execution_id: resume }), 2000);
  }, [resume, reqResume])

  useEffect(() => {
    wsExecutionRef.current.suscribe(`EXECUTION_STATUS_${flow.id}`);
  }, [userId])

  const onCallExecute = (data) =>
    historyRouter.push(`/chat_studio?root_node_id=${data.root_node_id}`);


  const onClickChangeAnimations = (value) => {
    setShowAnimations(value);
    setIsAnimationOptionShown(false);
    if (!value) {
      setNodes((nodes) => nodes.map((node) => {
        node.data.currentExecution = null;
        node.data.status = null;
        node.data.message = null;
        return node;
      }));
    }
  }

  const onClickGenerateFlow = (values) => {
    callGenerateFlow({
      flow_id: flow.id,
      page_id: activePageId,
      description: values.description,
    })
  }

  /*----------------------------------------------------------------*/
  /* Messages                                                       */
  /*----------------------------------------------------------------*/
  const onExecutionMessageReceived = (data) => {
    // messages.onExecutionMessageReceived(nodes, edges, data, showAnimations, wsNodeRef, setNodes, setCurrentExecutionId, setIsExecutionDetailsShown,
    //   setIsAnimationOptionShown, showExecutionToast, callSetRead, reqLatestExecutions)
  }

  const onNodeMessageReceived = (data) => {
    messages.onNodeMessageReceived(data, setNodes);
  }

  /*----------------------------------------------------------------*/
  /* Processes                                                      */
  /*----------------------------------------------------------------*/

  const onCreateProcess = () => {
    historyRouter.replace(`/flow`);
    reloadFlow();
  }

  /*----------------------------------------------------------------*/
  /* Edges                                                          */
  /*----------------------------------------------------------------*/

  // Function when a edge is connected to node
  const onClickConnectEdge = useCallback(({ source, target, sourceHandle, targetHandle }) => {
    board.connectEdges(source, target, sourceHandle, targetHandle, flow.id, activePageId, isCasePreview, setNodes, setEdges, updateHistory, callAutoSave);
  }, [edges, setEdges])

  // Function when a already connected edge is modified between two nodes
  const onClickUpdateEdge = useCallback((oldEdge, connection) => {
    board.updateEdge(oldEdge, connection, edges, flow.id, activePageId, isCasePreview, setNodes, setEdges, updateHistory, callAutoSave);
  }, [edges, setEdges])


  /*----------------------------------------------------------------*/
  /* Nodes                                                          */
  /*----------------------------------------------------------------*/

  // Function when a node is dragged over the board
  const onDragNode = useCallback((event) => {
    board.dragNode(event);
  }, []);

  // Function when a node is dragged over the board
  const onDropNode = (event) => {
    board.dropNode(event, reactFlowWrapper, reactFlowInstance, onClickNode, onClickAnnotation);
  }

  // Function to delete a single node
  const deleteNode = (id) => {

    let newEdges = [];
    setEdges((eds) => {
      newEdges = eds.filter((ed) => ed.source != id && ed.target != id);
      return newEdges;
    })

    setNodes((nds) => {
      const newNodes = nds.filter((nd) => nd.id != id);
      save.saveBoard(newNodes, newEdges, flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
      return newNodes;
    });

  }

  // Function when node is clicked on the toolbox panel
  const onClickNode = (props) => {

    const {
      actionId,
      initialPosition,
      parentId,
      position,
      mode,
      flipMode = false,
    } = props;

    const action = actions.find((action) => action.id == actionId);
    if (!action) return;

    const uiSettings = action.uiSettings;
    const fields = uiSettings.form?.fields ?? [];

    if (fields.length == 0) {
      createNode({}, actionId, initialPosition);
      return;
    }
    let isAnnotation = action.isAnnotation == "annotations" && action.name != "node_annotation";
    let url = !isAnnotation ? `/flow/create/${actionId}` : `/flow/create_annotation/${actionId}`;
    const params = [];

    if (parentId) params.push(`parent_id=${parentId}`);
    if (position) params.push(`position=${position}`);
    if (mode) params.push(`mode=${mode}`);
    if (flipMode) params.push("flip=true");

    if (initialPosition) {
      params.push(`x=${initialPosition.x}`);
      params.push(`y=${initialPosition.y}`);
    }

    if (params.length > 0) url += `?${params.join("&")}`;
    historyRouter.push(url);

    if (window.innerWidth < 540)
      setIsToolboxCollapsed(true)
  }

  // Callback function to create a node
  const createNode = (values, actionId, position = null, extraAttributes = {}) => {

    const keys = Object.keys(values);
    const input_values = keys.map((key) => {
      return { name: key, value: values[key] };
    });

    const action = actions.find(action => action.id == actionId);

    const newNode = {
      type: "node",
      isAnnotation: false,
      data: {
        action: { ...action },
        input_values: input_values,
        ...extraAttributes
      }
    }

    if (position)
      newNode.position = position;

    if (action && action.name == "node_annotation") localStorage.setItem(`last_annotation_${flow.id}`, action.id)

    save.saveBoard([...nodes, newNode], [...edges], flow.id, activePageId, isCasePreview, updateHistory, callAutoSave)
    historyRouter.replace(`/flow`);
  }

  // Callback function to edit a node
  const onClickEditNode = (values, remoteId, isDeletingNode = false) => {
    if (isDeletingNode) {
      const node = nodes.find((node) => node.remoteId == remoteId && node.isAnnotation == false);
      if (!node) return;
      deleteNode(node.id);
    } else {
      board.editNode(remoteId, values, edges, setNodes, flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
    }
    historyRouter.push(`/flow`);
  }

  // Function to close the modal of create/edit a node
  const onClickCloseModalRoute = () => {
    historyRouter.push(`/flow`);
  }

  // Fucntion to select a node on the board
  const onSelectNode = (id) => {
    setSelectedNodes((prevSelectedNodes) => {
      if (prevSelectedNodes.includes(id)) {
        return prevSelectedNodes.filter((node) => node != id)
      } else {
        return prevSelectedNodes.concat(id)
      }
    })
  }

  // Function to delete selected nodes on the board
  const onClickDeleteSelectedNodes = () => {

    let newEdges = [];
    setEdges((eds) => {
      let edgesToDelete = new Set();
      for (const node of selectedNodes) {
        for (const edge of eds) {
          if (edge.source == node || edge.target == node) {
            edgesToDelete.add(edge.remoteId)
          }
        }
      }
      edgesToDelete = Array.from(edgesToDelete);
      newEdges = eds.filter((ed) => {
        if (edgesToDelete.includes(ed.remoteId)) return false;
        return true;
      });
      return eds;
    })

    setNodes((nds) => {
      const newNodes = nds.filter((nd) => {
        if (selectedNodes.includes(nd.id)) return false;
        return true;
      });
      save.saveBoard(newNodes, newEdges, flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
      return nds;
    });

    setSelectedNodes([]);

  }

  // Function to delete proccesses with multiple nodes selected
  const onClickDeleteSelectedProcesses = () => {
    callDeleteNodesTree({
      "nodes": selectedNodes.map((node) => parseInt(node.split("-")[0])),
      "flow_id": flow.id,
    })
  }

  // Fucntion when a trigger changes its status
  const onClickChangeTriggerStatus = (id) => {

    setNodes((nodes) => {
      let actualNode = nodes.find((nd) => nd.id == id);
      actualNode.data.isEnabled = !actualNode.data.isEnabled;
      save.saveBoard(nodes, edges, flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
      return nodes;
    });
  }

  const onClickInsertChild = (params) => {
    setNodes((prevNodes) => {
      const newNodes = [...prevNodes].map(node => {
        node.data.insert_child_attrs = null;
        return node;
      });
      const parentId = params.node_id;
      const currentNode = newNodes.find(node => node.id == `${parentId}-node`)
      if (!currentNode) return newNodes;

      const nodeParams = {
        parentId: params.node_id,
        position: params.position,
        mode: params.mode,
      }

      setIsToolboxCollapsed(true);
      currentNode.data["insert_child_attrs"] = params;
      historyRouter.push(`/action_form?node_params=${encodeURIComponent(JSON.stringify(nodeParams))}`);
      return newNodes;
    })

  }

  const onEditTreeNode = (data) => {
    historyRouter.push(`/flow`);
    reloadFlow();
  }

  const onDeleteTreeNode = (data) => {
    historyRouter.replace(`/flow`);
    reloadFlow();
  }

  const centerNodeOnBoard = (nodeId) => {
    const node = nodes.find((node) => node.remoteId == nodeId);
    const x = node.position.x + 50;
    const y = node.position.y + 25;
    const zoom = getZoom();
    setCenter(x, y, { zoom: zoom, duration: 1000 });
  }

  const onClickDuplicateSelectedNodes = () => {
    const nodesToDuplicate = nodes.filter(node => selectedNodes.includes(node.id)).map(node => node?.remoteId);
    callDuplicateNodes({
      flow_id: flow.id,
      page_id: activePageId,
      nodes: nodesToDuplicate
    })
    setSelectedNodes([]);
  }

  /*----------------------------------------------------------------*/
  /* Annotations                                                    */
  /*----------------------------------------------------------------*/

  // Function when an annotation is clicked to put it
  const onClickAnnotation = (actionId, initialPosition = null) => {

    const action = actions.find((action) => action.id == actionId);
    if (!action) return;

    let url = `/flow/create_annotation/${actionId}`;
    if (initialPosition) url += `?x=${initialPosition.x}&y=${initialPosition.y}`;
    historyRouter.push(url);

  }

  // Callback function to create an annotation
  const createAnnotation = (values, actionId, position = null) => {

    const keys = Object.keys(values);
    const input_values = keys.map((key) => {
      return { name: key, value: values[key] };
    });

    const action = actions.find((action) => action.id == actionId);
    if (!action) return;

    const newNote = {
      type: action.name,
      isAnnotation: true,
      data: {
        action: { ...action },
        input_values: input_values
      }
    }

    if (position)
      newNote.position = position;

    localStorage.setItem(`last_annotation_${flow.id}`, actionId)
    save.saveBoard([...nodes, newNote], [...edges], flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
    historyRouter.replace(`/flow`);

  }

  // Callback function to edit an annotation
  const onClickEditAnnotation = (values, remoteId, isDeletingNode = false) => {
    if (isDeletingNode) {
      const annotation = nodes.find((node) => node.remoteId == remoteId && node.isAnnotation == true);
      if (!annotation) return;
      deleteNode(annotation.id);
    } else {
      board.editAnnotation(remoteId, values, edges, flow.id, activePageId, isCasePreview, setNodes, updateHistory, callAutoSave);
    }
    historyRouter.replace(`/flow`);

  }

  /*----------------------------------------------------------------*/
  /* Toolbox                                                        */
  /*----------------------------------------------------------------*/

  const onClickCloseToolbox = () => {
    setIsToolboxCollapsed(true);
    onClickInsertChild({});
  }

  const onClickOpenToolbox = () => {
    setIsToolboxCollapsed(false);
  }

  const onClickCollapseToolbox = () => {
    if (isToolboxCollapsed) onClickOpenToolbox()
    else onClickCloseToolbox()
  }

  /*----------------------------------------------------------------*/
  /* Right navigation                                                 */
  /*----------------------------------------------------------------*/

  const onClickCollapseRightNav = () => {
    setIsRightNavCollapsed(true);
  }

  const onClickVersions = () => {
    setIsRightNavCollapsed(!isRightNavCollapsed && rightNavActiveSection == "versions");
    setRightNavActiveSection("versions");
  }

  /*----------------------------------------------------------------*/
  /* General                                                        */
  /*----------------------------------------------------------------*/

  const onClickUndo = () => {
    if (flowHistoryPosition > 0) {
      setHistoryPosition((prev) => prev - 1);
      const lastState = flowHistory[flowHistoryPosition - 1];
      const nodes = [...lastState.nodes, ...lastState.annotations];
      setNodes(nodes);
      setEdges([...lastState.edges]);
      save.saveBoard(nodes, [...lastState.edges], flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
    }
  }

  const onClickRedo = () => {
    if (flowHistoryPosition < flowHistory.length - 1) {
      setHistoryPosition((prev) => prev + 1);
      const nextState = flowHistory[flowHistoryPosition + 1];
      const nodes = [...nextState.nodes, ...nextState.annotations];
      setNodes(nodes);
      setEdges([...nextState.edges]);
      save.saveBoard(nodes, [...nextState.edges], flow.id, activePageId, isCasePreview, updateHistory, callAutoSave);
    }
  }


  const onClickChangePage = pageId => {
    setActivePageId(pageId);
    sessionStorage.setItem("page_" + flow.id, pageId)
  }

  // Function to insert a node between two nodes already connected
  const onClickAttachNode = (edgeId, actionName) => {
    board.attachNode(edgeId, actionName, setEdges);
  }

  const [callCleanValidate, reqCleanValidate] = usePost('/flows/clean_validate', {
    onCompleted: (data) => {
      save.onAutoSave(data, selectedNodes, flow.id, activePageId, isCasePreview, setNodes, setEdges, updateHistory,
        onClickExecuteNode, onClickChangeTriggerStatus, onSelectNode, deleteNode, onClickInsertChild, callAutoSaveRef.current);
      onPostSave();
    }
  });

  const [callAutoSave, reqAutoSave] = usePost('/flows/save', {
    onCompleted: (data) => {
      save.onAutoSave(data, selectedNodes, flow.id, activePageId, isCasePreview, setNodes, setEdges, updateHistory,
        onClickExecuteNode, onClickChangeTriggerStatus, onSelectNode, deleteNode, onClickInsertChild, callAutoSaveRef.current);
      callCleanValidate({ flow_id: flow.id, page_id: activePageId })
    }
  });

  const [callSaveUi, reqSaveUi] = usePost('/flows/save_ui', {
    onCompleted: () => {
      onPostSave();
    }
  });

  const [callDuplicateNodes, reqDuplicateNodes] = usePost('/flows/duplicate_nodes', {
    onCompleted: (data) => {
      reloadFlow();
    }
  });

  const isFlowBeingSaved = reqAutoSave.loading;
  const isGeneratingFlow = reqGenerateFlow.loading;
  const latestExecutions = reqLatestExecutions.data ?? [];
  callAutoSaveRef.current = callAutoSave
  let alertStatusColor = null;
  latestExecutions.forEach((execution) => {
    if (alertStatusColor != null) return;
    if (execution.status == "AWAITING") alertStatusColor = "warning";
    if (execution.status == "OK") alertStatusColor = "success";
  });

  // Export functions to be used by parent component
  useImperativeHandle(ref, () => ({
    createNode,
    centerNodeOnBoard,
    onClickEditNode,
    createAnnotation,
    onClickEditAnnotation,
    onClickCloseModalRoute,
    onEditTreeNode,
    onDeleteTreeNode,
    onCreateProcess
  }));

  return (
    <View
      flow={flow}
      pages={pages}
      nodes={nodes}
      edges={edges}
      user={user}
      selectedNodes={selectedNodes}
      activePageId={activePageId}
      reactFlowWrapper={reactFlowWrapper}
      currentExecutionId={currentExecutionId}
      wsExecutionRef={wsExecutionRef}
      wsNodeRef={wsNodeRef}
      toolboxRef={toolboxRef}
      showAnimations={showAnimations}
      alertStatusColor={alertStatusColor}
      toolboxActiveSection={toolboxActiveSection}
      rightNavActiveSection={rightNavActiveSection}
      isToolboxCollapsed={isToolboxCollapsed}
      isRightNavCollapsed={isRightNavCollapsed}
      isFlowBeingSaved={isFlowBeingSaved}
      isGeneratingFlow={isGeneratingFlow}
      isOwner={isOwner}
      isValidator={isValidator}
      isEditor={isEditor}
      isDeveloper={isDeveloper}
      isAnonymous={isAnonymous}
      isContactUsShown={isContactUsShown}
      isExecutionDetailsShown={isExecutionDetailsShown}
      isAnimationOptionShown={isAnimationOptionShown}
      isCasePreview={isCasePreview}
      setIsContactUsShown={setIsContactUsShown}
      setReactFlowInstance={setReactFlowInstance}
      setIsExecutionDetailsShown={setIsExecutionDetailsShown}
      setToolboxActiveSection={setToolboxActiveSection}
      onDropNode={onDropNode}
      onDragNode={onDragNode}
      onClickConnectEdge={onClickConnectEdge}
      onClickUpdateEdge={onClickUpdateEdge}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onClickNode={onClickNode}
      createNode={createNode}
      createAnnotation={createAnnotation}
      onClickEditAnnotation={onClickEditAnnotation}
      onClickAnnotation={onClickAnnotation}
      onClickUndo={onClickUndo}
      onClickRedo={onClickRedo}
      onClickChangePage={onClickChangePage}
      onClickDeleteSelectedNodes={onClickDeleteSelectedNodes}
      onClickDeleteSelectedProcesses={onClickDeleteSelectedProcesses}
      onClickExecuteNode={onClickExecuteNode}
      onExecutionMessageReceived={onExecutionMessageReceived}
      onNodeMessageReceived={onNodeMessageReceived}
      onClickChangeAnimations={onClickChangeAnimations}
      onClickGenerateFlow={onClickGenerateFlow}
      onClickOpenToolbox={onClickOpenToolbox}
      onClickCloseToolbox={onClickCloseToolbox}
      onClickCollapseToolbox={onClickCollapseToolbox}
      onClickCollapseRightNav={onClickCollapseRightNav}
      onClickVersions={onClickVersions}
      onClickDuplicateSelectedNodes={onClickDuplicateSelectedNodes}
      reloadFlow={reloadFlow}
    />
  );

});

Controller.displayName = "Controller";

export default Controller;